import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Flex } from '../../../components/Flex';
import { PaidFeatureNotice } from '../../../components/PaidFeatureNotice';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import i18n, { AppLangs } from '../../../i18n';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { AtiraLinkPlanEmbed } from '../../../model/plan/types/AtiraLinkPlanEmbed.enum';
import { AtiraLinkPlanType } from '../../../model/plan/types/AtiraLinkPlanType.enum';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { User } from '../../../model/user/User';
import { useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Spacing } from '../../../theme/Spacing';
import { LinkTextarea } from './LinkTextarea';

const getCanUserEmbed = (user: User) => {
  switch (user.plan?.perks.embed) {
    case AtiraLinkPlanEmbed.PLAN_0:
    case AtiraLinkPlanEmbed.PLAN_1:
      return false;

    case AtiraLinkPlanEmbed.PLAN_2:
      return true;
  }
};

const StyledAnchor = styled.a`
  color: ${({ theme }) => theme.main};
  text-underline-offset: 5px;
`;

const TextareaWrapper = styled(Flex)`
  textarea {
    direction: ltr;
  }

  ${i18n.language === AppLangs.AR
    ? `
    @media (min-width: ${Breakpoints.DESKTOP}) {
      p {
        padding-left: ${Spacing.s}
      }
    }
  `
    : ''}
`;

export const AtiraLinkContentEmbed: React.FC = () => {
  const { t } = useTranslation();

  const { control, clearErrors } = useFormContext<CreateAtiraLinkDto>();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const canEmbed = getCanUserEmbed(user);

  return (
    <Section title={t('atira_link.create.content.embed.title')}>
      <Flex flexDirection="column" gap="s">
        <Text>
          {t('atira_link.create.content.embed.description-before')}
          <code>iframe</code>
          {t('atira_link.create.content.embed.description-after')}
        </Text>

        <Text>
          {t('atira_link.create.content.embed.note')}
          <StyledAnchor
            href="https://atiraspace.com"
            target="_blank"
            rel="noreferrer"
          >
            {t('common.atiraspace')}
          </StyledAnchor>
        </Text>
      </Flex>

      <Controller
        control={control}
        rules={{
          maxLength: {
            value: Lengths.LONG,
            message: t('error.length.less', {
              name: t('common.embed'),
              length: Lengths.LONG,
            }),
          },
        }}
        name="embed"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <TextareaWrapper>
            <LinkTextarea
              title={t('common.embed')}
              value={value}
              onChange={onChange}
              placeholder={'<iframe src="https://yourdomain.com"></iframe>'}
              maxLength={Lengths.LONG}
              disabled={!canEmbed}
              dataTestId={atiraLinkTestIDs.content.embed}
              valid={!error}
              errorMessage={error?.message}
              onFocus={() => clearErrors('embed')}
            />
          </TextareaWrapper>
        )}
        disabled={!canEmbed}
      />
      {!canEmbed ? <PaidFeatureNotice plan={AtiraLinkPlanType.PLAN_2} /> : null}
    </Section>
  );
};
